'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, CheckCircle, Zap, Lightbulb, Cpu, Battery, Wind } from "lucide-react";
import { useParams } from "next/navigation";
// import { localizeSpelling } from "@/lib/spelling-localization";

// Temporary fallback function
const localizeSpelling = (text: string, locale: string) => text;

// Safe localization helper that falls back to original text if localization fails
const safeLocalizeSpelling = (text: string, locale: string) => {
  try {
    return localizeSpelling(text, locale);
  } catch (error) {
    console.warn(`Localization failed for "${text}" in locale "${locale}":`, error);
    return text;
  }
};

const smartGridData = {
  title: "Smart Grid Optimization",
  subtitle: "AI-powered demand forecasting with weather and consumption pattern analysis, real-time grid management with automated load balancing.",
  headerGradient: "bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500",
  accentColor: "text-green-600",
  buttonColor: "bg-green-600 hover:bg-green-700",
  challenge: "Energy providers face increasing demands for reliability, sustainability, and cost efficiency while integrating renewable sources and managing peak loads.",
  solutionHighlights: [
    "AI-powered demand forecasting with weather and consumption pattern analysis",
    "Real-time grid management with automated load balancing",
    "Predictive maintenance for critical infrastructure components",
    "Renewable energy integration optimization algorithms"
  ],
  benefits: [
    "30-35% improved grid reliability",
    "25% reduction in energy waste",
    "40% better renewable energy utilization",
    "Reduced operational costs through predictive analytics"
  ],
  techStack: ["Machine Learning", "IoT Sensors", "Cloud Computing", "Data Visualization", "Predictive Analytics", "Edge Computing"],
  gridStats: {
    renewablePercentage: "62%",
    gridEfficiency: "94.5%",
    loadBalance: "Optimal",
    forecastAccuracy: "94%"
  }
};

export default function SmartGridOptimizationPage() {
  // Since this is a client component, we use useParams hook
  const params = useParams();
  const currentLocale = (params?.locale as string) || 'en';
  
  // Localize all text content based on current locale
  const localizedData = {
    ...smartGridData,
    title: safeLocalizeSpelling(smartGridData.title, currentLocale),
    subtitle: safeLocalizeSpelling(smartGridData.subtitle, currentLocale),
    challenge: safeLocalizeSpelling(smartGridData.challenge, currentLocale),
    solutionHighlights: smartGridData.solutionHighlights.map(
      highlight => safeLocalizeSpelling(highlight, currentLocale)
    ),
    benefits: smartGridData.benefits.map(
      benefit => safeLocalizeSpelling(benefit, currentLocale)
    ),
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header Section */}
      <header className={`py-16 md:py-24 text-white ${localizedData.headerGradient}`}>
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <Link href={`/${currentLocale}/portfolio`} className="inline-flex items-center text-sm text-white hover:text-emerald-100 transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
              <ArrowLeft size={18} className="mr-2" />
              {safeLocalizeSpelling("Back to Portfolio", currentLocale)}
            </Link>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {localizedData.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-emerald-100 max-w-3xl mx-auto">
            {localizedData.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-3 justify-center">
            {localizedData.techStack.slice(0, 3).map(tech => (
              <span key={tech} className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-medium">
                {tech}
              </span>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-8 md:p-12 rounded-xl shadow-xl -mt-20 md:-mt-32 relative z-10">
          
          {/* Smart Grid Dashboard Visual Section */}
          <section className="mb-12">
            <h2 className={`text-3xl font-semibold mb-8 text-center ${localizedData.accentColor}`}>
              {safeLocalizeSpelling("Smart Grid Control Center", currentLocale)}
            </h2>
            <div className="bg-slate-100 p-4 sm:p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between px-4 py-2 mb-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white text-sm font-semibold rounded-t-md shadow">
                <span>{safeLocalizeSpelling("Grid Optimization Engine", currentLocale)}</span>
                <span className="text-xs bg-green-500/90 px-2 py-0.5 rounded-full flex items-center">
                  <Zap size={12} className="mr-1"/>
                  {safeLocalizeSpelling("Active", currentLocale)}
                </span>
              </div>
              <div className="flex flex-col md:flex-row gap-4 min-h-[280px]">
                {/* Smart Grid Dashboard Frame */}
                <div className="flex-1 flex items-center justify-center bg-slate-200 rounded-md p-4 relative min-h-[200px]">
                  <div className="w-full h-full bg-white rounded-lg relative overflow-hidden shadow-inner border border-slate-300 flex flex-col p-4">
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <Cpu size={18} className="text-green-500 mr-1" />
                        <span className="text-slate-700 font-medium">{safeLocalizeSpelling("Smart Grid Network", currentLocale)}</span>
                      </div>
                      <div className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                        {safeLocalizeSpelling("Optimized", currentLocale)}
                      </div>
                    </div>
                    
                    <div className="mt-10 grid grid-cols-2 gap-4">
                      {/* Energy Sources Chart */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-xs font-medium text-slate-600 mb-2">{safeLocalizeSpelling("Renewable Sources", currentLocale)}</div>
                        <div className="relative h-24 mb-1">
                          {/* Stacked bar chart representation */}
                          <div className="absolute bottom-0 left-0 right-0 flex h-full rounded-sm overflow-hidden">
                            <div className="bg-green-400 w-[42%] h-full flex items-end justify-center">
                              <span className="text-xs text-white font-medium pb-1">42%</span>
                            </div>
                            <div className="bg-yellow-400 w-[20%] h-full flex items-end justify-center">
                              <span className="text-xs text-white font-medium pb-1">20%</span>
                            </div>
                            <div className="bg-blue-400 w-[23%] h-full flex items-end justify-center">
                              <span className="text-xs text-white font-medium pb-1">23%</span>
                            </div>
                            <div className="bg-slate-400 w-[15%] h-full flex items-end justify-center">
                              <span className="text-xs text-white font-medium pb-1">15%</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex text-xs justify-between text-slate-600">
                          <div className="flex items-center"><div className="w-2 h-2 bg-green-400 mr-1 rounded-full"></div> {safeLocalizeSpelling("Wind", currentLocale)}</div>
                          <div className="flex items-center"><div className="w-2 h-2 bg-yellow-400 mr-1 rounded-full"></div> {safeLocalizeSpelling("Solar", currentLocale)}</div>
                          <div className="flex items-center"><div className="w-2 h-2 bg-blue-400 mr-1 rounded-full"></div> {safeLocalizeSpelling("Hydro", currentLocale)}</div>
                          <div className="flex items-center"><div className="w-2 h-2 bg-slate-400 mr-1 rounded-full"></div> {safeLocalizeSpelling("Other", currentLocale)}</div>
                        </div>
                      </div>
                      
                      {/* Demand Prediction */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-xs font-medium text-slate-600 mb-2">{safeLocalizeSpelling("AI Forecasting", currentLocale)}</div>
                        <div className="relative h-24 mb-1">
                          {/* Line chart representation */}
                          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="h-full w-full">
                            <polyline 
                              points="0,35 10,32 20,28 30,30 40,20 50,25 60,15 70,10 80,15 90,5 100,15" 
                              fill="none" 
                              stroke="#10b981" 
                              strokeWidth="2"
                              vectorEffect="non-scaling-stroke"
                            />
                            <polyline 
                              points="0,35 10,32 20,28 30,30 40,20 50,25 60,15 70,10 80,15 90,5 100,15" 
                              fill="rgba(16, 185, 129, 0.1)" 
                              stroke="none"
                              strokeWidth="0"
                              vectorEffect="non-scaling-stroke"
                            />
                          </svg>
                        </div>
                        <div className="flex justify-between text-xs text-slate-600">
                          <span>{safeLocalizeSpelling("Now", currentLocale)}</span>
                          <span>{safeLocalizeSpelling("6h", currentLocale)}</span>
                          <span>{safeLocalizeSpelling("12h", currentLocale)}</span>
                          <span>{safeLocalizeSpelling("18h", currentLocale)}</span>
                          <span>{safeLocalizeSpelling("24h", currentLocale)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex gap-3 justify-between">
                      <div className="flex items-center gap-1 bg-green-50 p-1.5 rounded-md text-xs text-green-700">
                        <Wind size={14} />
                        <span>{safeLocalizeSpelling("Wind Capacity: 86%", currentLocale)}</span>
                      </div>
                      <div className="flex items-center gap-1 bg-blue-50 p-1.5 rounded-md text-xs text-blue-700">
                        <Battery size={14} />
                        <span>{safeLocalizeSpelling("Storage: 73%", currentLocale)}</span>
                      </div>
                      <div className="flex items-center gap-1 bg-amber-50 p-1.5 rounded-md text-xs text-amber-700">
                        <Lightbulb size={14} />
                        <span>{safeLocalizeSpelling("Load: 68%", currentLocale)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Grid Stats */}
                <div className="w-full md:w-52 bg-slate-50 rounded-md p-4 space-y-2.5 ring-1 ring-slate-200 flex flex-col justify-center">
                  {[ 
                    { label: safeLocalizeSpelling("Renewable Energy", currentLocale), value: localizedData.gridStats.renewablePercentage, color: "text-green-700", bgColor:"bg-green-100" }, 
                    { label: safeLocalizeSpelling("Grid Efficiency", currentLocale), value: localizedData.gridStats.gridEfficiency, color: "text-blue-700", bgColor:"bg-blue-100" }, 
                    { label: safeLocalizeSpelling("Load Balance", currentLocale), value: localizedData.gridStats.loadBalance, color: "text-slate-700", bgColor:"bg-slate-200" }, 
                    { label: safeLocalizeSpelling("Forecast Accuracy", currentLocale), value: localizedData.gridStats.forecastAccuracy, color: "text-amber-700", bgColor:"bg-amber-100" } 
                  ].map(stat => (
                    <div key={stat.label} className={`flex items-center justify-between p-2 rounded ${stat.bgColor}`}>
                      <span className={`text-xs font-medium ${stat.color}`}>{stat.label}</span>
                      <span className={`font-bold text-sm ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-1.5 mt-3 bg-slate-200 border-t border-slate-300 text-xs text-slate-600 flex items-center gap-2 rounded-b-md">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse"></span>
                <span>{safeLocalizeSpelling("Grid optimization active. Last update: 3 seconds ago.", currentLocale)}</span>
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-1 gap-10">
            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("The Challenge", currentLocale)}</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {localizedData.challenge}
              </p>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Our Solution", currentLocale)}</h2>
              <ul className="space-y-3 text-slate-700">
                {localizedData.solutionHighlights.map((item, index) => (
                  <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start pl-2">
                    <CheckCircle className={`w-5 h-5 ${localizedData.accentColor} mr-3 mt-0.5 flex-shrink-0`} />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-4 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Key Benefits", currentLocale)}</h2>
              <div className="bg-green-50 border border-green-200 p-6 rounded-lg shadow-sm">
                <ul className="space-y-3">
                  {localizedData.benefits.map((benefit, index) => (
                    <motion.li key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className={`flex items-start text-green-800`}>
                      <CheckCircle className={`w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0`} />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className={`text-2xl font-semibold mb-5 ${localizedData.accentColor}`}>{safeLocalizeSpelling("Technologies Used", currentLocale)}</h2>
              <div className="flex flex-wrap gap-3">
                {localizedData.techStack.map((tech, index) => (
                  <motion.span key={index} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.05 }}
                    className="bg-slate-200 text-slate-700 px-4 py-1.5 rounded-full text-sm font-medium border border-slate-300 hover:bg-slate-300 transition-colors cursor-default">
                    {tech}
                  </motion.span>
                ))}
              </div>
            </section>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
            <Link href={`/${currentLocale}/contact`} className={`${localizedData.buttonColor} text-white font-semibold py-3.5 px-10 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50`}>
              {safeLocalizeSpelling("Optimize Your Energy Grid", currentLocale)}
            </Link>
          </motion.div>

        </motion.div>
      </main>
    </div>
  );
} 